import { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { getDefaultLevelization } from '@/utils/pathwayUtils';
import { useNodeById } from '@/stores/analysisStore';
const PlotSyncContext = createContext(null);

const normalizeLevelizationForNode = (formData, node, defaultValues = {}) => {
  const hasLevelization =
    node && ['levelizeBy', 'levelizeUnit'].some(key => key in formData && formData[key] !== undefined);

  if (!hasLevelization) {
    return formData;
  }

  const { levelizeBy, levelizeUnit } = getDefaultLevelization(
    node,
    formData.levelizableIos ?? defaultValues.levelizableIos,
    formData.levelizeBy,
    formData.levelizeUnit,
  );

  return {
    ...formData,
    levelizeBy,
    levelizeUnit,
  };
};

export const PlotSyncProvider = ({ children, paramKeys = [] }) => {
  const [params, setParams] = useState(null);

  const value = {
    params,
    setParams,
    paramKeys,
  };

  return <PlotSyncContext.Provider value={value}>{children}</PlotSyncContext.Provider>;
};

PlotSyncProvider.propTypes = {
  children: PropTypes.node.isRequired,
  paramKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePlotSync = () => {
  const context = useContext(PlotSyncContext);
  if (!context) {
    throw new Error('usePlotSync must be used within a PlotSyncProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePlotSyncHandler = (onSync, defaultValues = {}) => {
  const { params, paramKeys } = usePlotSync();
  const node = useNodeById(params?.nodeId);

  const syncData = async () => {
    if (!params) {
      return;
    }

    const hasChanged = paramKeys.some(key => params[key] !== defaultValues[key]);

    if (!hasChanged) {
      return;
    }

    const syncedParams = paramKeys.reduce(
      (acc, key) => ({
        ...acc,
        ...(key in params ? { [key]: params[key] } : {}),
      }),
      {},
    );

    const formData = normalizeLevelizationForNode(
      { ...defaultValues, nodeId: params.nodeId, ...syncedParams },
      node,
      defaultValues,
    );
    await onSync(formData);
  };

  return { syncData };
};
